import { supabase } from './supabase';
import { checkSupabaseConnection, checkUserProfileExists } from './supabase';
import notificationService from './notificationService';
import emailService from './emailService';
import cacheService from '../services/cacheService.js';

const authService = {
  // Check if email already exists in the system - INTERNAL USE ONLY
  // This should never be exposed directly to frontend routes/components
  checkEmailExists: async (email) => {
    try {
      // Standardize the email format
      const normalizedEmail = email.trim().toLowerCase();
      
      // Try to check if user exists via RLS-safe profile lookup
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', normalizedEmail)
        .maybeSingle();
        
      if (error) {
        console.error('Error checking email in user_profiles:', error);
        // Don't return early on error, continue to check auth.users
      } else if (data) {
        // Email exists in user_profiles
        return { exists: true };
      }
      
      try {
        // Instead of using dummy password auth (which can produce false positives),
        // use the RPC function to check for orphaned auth records
        const { data: orphanCheck, error: rpcError } = await supabase
          .rpc('check_email_orphaned', { p_email: normalizedEmail });
          
        // Handle RPC function not existing (404 error)
        if (rpcError && (rpcError.code === '42883' || rpcError.message.includes('function') || rpcError.message.includes('404'))) {
          // Fallback to the old dummy password method
        } else if (orphanCheck?.exists_in_auth && orphanCheck?.is_orphaned) {
          // This is a confirmed orphaned auth record
          return { exists: true, orphaned: true };
        } else if (orphanCheck?.exists_in_auth && !orphanCheck?.is_orphaned) {
          // This exists in auth AND has a profile, so definitely exists
          return { exists: true };
        } else if (!orphanCheck?.exists_in_auth) {
          // Confirmed doesn't exist in auth
          return { exists: false };
        }
        
        // Fallback to old method if RPC fails or doesn't exist
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password: 'INTENTIONALLY_WRONG_PASSWORD_FOR_EMAIL_CHECK'
        });
        
        // Be more conservative about "Invalid login credentials" - might be a false positive
        if (signInError && signInError.message.includes('Invalid login credentials')) {
          // Don't immediately assume it exists - mark as uncertain
          return { exists: false, uncertain: true };
        }
        
        // If we get "Email not confirmed" error, the email exists but isn't verified
        if (signInError && signInError.message.includes('Email not confirmed')) {
          return { exists: true };
        }
        
        // If we get "User not found" or similar, email doesn't exist
        if (signInError && 
            (signInError.message.includes('User not found') ||
             signInError.message.includes('Invalid user'))) {
          return { exists: false };
        }
        
        // For any other error, we can't be certain - err on the side of letting user try to register
        return { exists: false, uncertain: true };
      } catch (authCheckError) {
        console.error('Error checking auth.users:', authCheckError);
        // If we got an error checking auth.users, we can't be sure, so safer to say user doesn't exist
        return { exists: false, uncertain: true };
      }
    } catch (error) {
      console.error('Error in checkEmailExists:', error);
      // If we hit an unexpected error, safer to say user doesn't exist
      return { exists: false, error: error.message };
    }
  },

  // Enhanced sign in with email notifications and better error handling
  signIn: async (email, password) => {
    let needsVerification = false;
    let userEmail = email.trim().toLowerCase();
    
    try {
      // Validate input parameters to prevent malformed requests
      if (!email || typeof email !== 'string' || !email.trim()) {
        return {
          success: false,
          error: 'Email is required.',
          type: 'invalid_input'
        };
      }

      if (!password || typeof password !== 'string' || password.trim().length === 0) {
        return {
          success: false,
          error: 'Password is required.',
          type: 'invalid_input'
        };
      }

      // Validate password length to prevent 400 errors
      if (password.length < 6) {
        return {
          success: false,
          error: 'Password must be at least 6 characters long.',
          type: 'invalid_input'
        };
      }

      // First check Supabase connectivity
      const connectionCheck = await checkSupabaseConnection();
      if (!connectionCheck.connected) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase connection failed during sign in:', connectionCheck.error);
        }
        return { 
          success: false, 
          error: connectionCheck.error || 'Unable to connect to authentication service. Please check your internet connection and try again.',
          type: connectionCheck.type || 'connection_error'
        };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password.toString()
      });

      if (error) {
        // Log error details for debugging 400 errors
        if (import.meta.env.DEV) {
          console.error('[AuthService] Sign in error details:', {
            status: error.status,
            message: error.message,
            cause: error.cause,
            email: email.trim().toLowerCase(),
            passwordLength: password.length,
            timestamp: new Date().toISOString()
          });
        }

        // DISABLED: Send failure notification
        // Login failure emails disabled to reduce email noise
        // await notificationService.notifyLoginFailure(
        //   email.trim().toLowerCase(), 
        //   error.message
        // );

        // Enhanced error handling with specific user-friendly messages
        // Check for email confirmation errors FIRST (before generic 400 handling)
        // Check error.code (Supabase SDK v2.39+) and error.message patterns
        if (error.code === 'email_not_confirmed' ||
            error.message?.includes('Email not confirmed') || 
            error.message?.includes('email not confirmed') ||
            error.message?.includes('not confirmed') ||
            error.message?.includes('confirmation required') ||
            error.message?.includes('email confirmation') ||
            error.message?.includes('verify your email') ||
            error.message?.includes('email verification')) {
          return { 
            success: false, 
            error: 'Please check your email and click the confirmation link before signing in.',
            type: 'email_not_confirmed'
          };
        }

        if (error.message.includes('Invalid login credentials')) {
          return { 
            success: false, 
            error: 'Invalid email or password. Please check your credentials and try again.',
            type: 'invalid_credentials'
          };
        }

        // Check for 400 Bad Request errors which often indicate malformed requests
        if (error.status === 400) {
          // Try to determine the specific cause
          if (error.message.includes('Invalid')) {
            return {
              success: false,
              error: 'Invalid email or password format. Please check your credentials and try again.',
              type: 'invalid_credentials'
            };
          }
          return {
            success: false,
            error: 'Unable to process login request. Please verify your email and password are correct.',
            type: 'bad_request'
          };
        }
        
        if (error.message.includes('Too many requests')) {
          return { 
            success: false, 
            error: 'Too many login attempts. Please wait a few minutes before trying again.',
            type: 'rate_limited'
          };
        }
        if (error.message.includes('User not found')) {
          return { 
            success: false, 
            error: 'No account found with this email address. Please check your email or sign up for a new account.',
            type: 'user_not_found'
          };
        }
        return { 
          success: false, 
          error: error.message,
          type: 'auth_error'
        };
      }

      // Verify user profile exists and create if missing
      if (data?.user) {
        const profileCheck = await checkUserProfileExists(data.user.id);
        
        if (!profileCheck.exists && !profileCheck.error) {
          // Create profile if it doesn't exist
          const profileData = {
            email: data.user.email,
            full_name: data.user.user_metadata?.full_name || 
                      data.user.user_metadata?.first_name + '' + data.user.user_metadata?.last_name || data.user.email?.split('@')[0] || 'User',
            role: data.user.user_metadata?.role || 'va',
            is_verified: false // New profiles start as unverified
          };
          
          const createResult = await authService.createUserProfile(data.user.id, profileData);
          if (!createResult.success) {
            return {
              success: false,
              error: 'Login successful but failed to create user profile. Please contact support.',
              type: 'profile_creation_failed'
            };
          }
        }

        // Check if user profile is verified and sync with Supabase auth status
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('is_verified, email')
            .eq('id', data.user.id)
            .single();

          if (profileError) {
            console.error('Error checking profile verification status:', profileError);
          } else if (profileData) {
            // Check if Supabase auth shows email as confirmed but our profile doesn't
            const isAuthEmailConfirmed = data.user.email_confirmed_at !== null;
            userEmail = profileData.email || data.user.email;
            
            if (isAuthEmailConfirmed && !profileData.is_verified) {
              // Sync the verification status - Supabase auth confirms email is verified
              try {
                await supabase
                  .from('user_profiles')
                  .update({ 
                    is_verified: true,
                    updated_at: new Date().toISOString() 
                  })
                  .eq('id', data.user.id);
                needsVerification = false; // Just synced, so verified now
              } catch (syncError) {
                console.error('Error syncing verification status:', syncError);
                // Continue with login - don't block user if sync fails
                needsVerification = !isAuthEmailConfirmed;
              }
            } else if (!profileData.is_verified) {
              // Profile shows unverified - check if auth email is confirmed
              if (isAuthEmailConfirmed) {
                // Auth email is confirmed but profile isn't synced - sync it
                try {
                  await supabase
                    .from('user_profiles')
                    .update({ 
                      is_verified: true,
                      updated_at: new Date().toISOString() 
                    })
                    .eq('id', data.user.id);
                  needsVerification = false;
                } catch (syncError) {
                  console.error('Error syncing verification status:', syncError);
                  needsVerification = true;
                }
              } else {
                // Neither auth nor profile is verified
                needsVerification = true;
              }
            } else {
              // Profile is verified - user is good to go
              needsVerification = false;
            }
          }
        } catch (verificationCheckError) {
          console.error('Error checking verification status:', verificationCheckError);
          // Continue with login if we can't check verification status
        }

        // Check user moderation status
        try {
          const { data: moderationStatus, error: moderationError } = await supabase
            .from('user_moderation_status')
            .select('status, reason, expires_at')
            .eq('user_id', data.user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (moderationError) {
            console.error('Error checking moderation status:', moderationError);
            // Continue with login if we can't check moderation status
          } else if (moderationStatus) {
            // Check if user is suspended or banned
            if (moderationStatus.status === 'suspended') {
              // Check if suspension has expired
              const now = new Date();
              const expiresAt = moderationStatus.expires_at ? new Date(moderationStatus.expires_at) : null;
              
              if (expiresAt && now > expiresAt) {
                // Suspension has expired - automatically reactivate the user
                try {
                  await supabase
                    .from('user_moderation_status')
                    .upsert({
                      user_id: data.user.id,
                      status: 'active',
                      reason: 'Suspension period expired',
                      moderator_id: data.user.id // System auto-reactivation
                    });
                  console.log('User suspension expired and was automatically lifted');
                  // Continue with login
                } catch (reactivateError) {
                  console.error('Error auto-reactivating expired suspension:', reactivateError);
                  // Continue with login anyway since suspension expired
                }
              } else {
                // Suspension is still active
                try {
                  await supabase.auth.signOut();
                } catch (signOutError) {
                  console.error('Error signing out suspended user:', signOutError);
                }
                
                const expiryMessage = expiresAt 
                  ? ` Your account will be reactivated on ${expiresAt.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}.`
                  : '';
                
                return {
                  success: false,
                  error: `Your account has been suspended. Reason: ${moderationStatus.reason || 'Violation of Community Guidelines'}.${expiryMessage} Contact support@vasprouts.com for more information.`,
                  type: 'account_suspended',
                  userEmail
                };
              }
            } else if (moderationStatus.status === 'banned') {
              // Account is permanently banned
              try {
                await supabase.auth.signOut();
              } catch (signOutError) {
                console.error('Error signing out banned user:', signOutError);
              }
              
              return {
                success: false,
                error: `Your account has been permanently banned. Reason: ${moderationStatus.reason || 'Severe or repeated violations of Terms of Service'}. If you believe this was a mistake, contact support@vasprouts.com.`,
                type: 'account_banned',
                userEmail
              };
            }
            // If status is 'warned', allow login but the warning is logged in their account history
          }
        } catch (moderationCheckError) {
          console.error('Error checking moderation status:', moderationCheckError);
          // Continue with login if we can't check moderation status
        }

        // Login security email notifications disabled
        // Previously sent login notifications via both Resend and legacy notification service
        // These were sending emails on every login which was excessive for user experience
        
        // DISABLED: Send success notification using Resend (non-blocking)
        // try {
        //   // Get user profile for full name
        //   const profileResult = await authService.getUserProfile(data.user.id);
        //   const fullName = profileResult.data?.full_name || data.user.email?.split('@')[0] || 'User';
        //   
        //   // Send login notification with Resend
        //   await emailService.sendLoginNotification(
        //     data.user.email,
        //     fullName,
        //     {
        //       timestamp: new Date().toISOString(),
        //       userAgent: navigator?.userAgent || 'Unknown',
        //       location: 'Unknown', // Could be enhanced with geolocation
        //       ipAddress: 'Hidden for privacy'
        //     }
        //   );
        // // eslint-disable-next-line no-unused-vars
        // } catch (_) {
        //   // Failed to send login notification email, continue with login process
        // }

        // DISABLED: Also send to the legacy notification service
        // notificationService.notifyLoginSuccess(
        //   data.user.email,
        //   { 
        //     userId: data.user.id,
        //     loginMethod: 'email_password'
        //   }
        // ).catch(() => {});
      }

      // If user needs verification, sign them out and return error
      if (needsVerification) {
        try {
          await supabase.auth.signOut();
        } catch (signOutError) {
          console.error('Error signing out unverified user:', signOutError);
        }
        
        return { 
          success: false, 
          error: 'Please check your email and click the confirmation link before signing in.',
          type: 'email_not_confirmed',
          userEmail 
        };
      }

      return { 
        success: true, 
        data,
        userEmail 
      };
    } catch (error) {
      // Network and infrastructure error handling
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError') ||
          error?.message?.includes('NetworkError')) {
        
        // DISABLED: Attempt to send failure notification
        // Login failure emails disabled to reduce email noise
        // try {
        //   await notificationService.notifyLoginFailure(
        //     email.trim().toLowerCase(), 
        //     'Network connectivity issue'
        //   );
        // // eslint-disable-next-line no-unused-vars
        // } catch (_) {
        //   // Failed to send network error notification, continue with error response
        // }

        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Please check your internet connection or try again later. If the problem persists, your Supabase project may be paused.',
          type: 'network_error'
        };
      }
      
      // Handle unexpected sign in error
      return { 
        success: false, 
        error: 'An unexpected error occurred during login. Please try again.',
        type: 'unexpected_error'
      };
    }
  },

  // Verify reCAPTCHA Enterprise token (server-side validation)
  verifyRecaptcha: async (recaptchaToken, action = 'register') => {
    try {
      if (!recaptchaToken) {
        return { success: false, error: 'reCAPTCHA token is required' };
      }

      console.log('🔒 Verifying reCAPTCHA Enterprise token...');

      // Use Supabase Edge Function for server-side verification
      const { data, error } = await supabase.functions.invoke('verify-recaptcha', {
        body: {
          token: recaptchaToken,
          action: action
        }
      });

      if (error) {
        console.error('reCAPTCHA verification error:', error);
        return {
          success: false,
          error: 'reCAPTCHA verification service unavailable. Please try again.'
        };
      }

      if (!data.success) {
        console.log('reCAPTCHA verification failed:', data.error);
        return {
          success: false,
          error: data.error || 'reCAPTCHA verification failed'
        };
      }

      console.log('✅ reCAPTCHA Enterprise verification successful. Score:', data.score);
      return {
        success: true,
        score: data.score
      };
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      return { success: false, error: 'reCAPTCHA verification failed' };
    }
  },

  // Enhanced sign up with Resend email verification and immediate profile creation
  signUp: async (email, password, userData = {}) => {
    try {
      console.log('🚀 Starting registration process for:', email);
      
      // Check if reCAPTCHA was already verified (score will be present if verified)
      if (userData.recaptchaScore !== undefined) {
        console.log('✅ Using pre-verified reCAPTCHA score:', userData.recaptchaScore);
        // reCAPTCHA already verified in frontend, no need to verify again
      } else if (userData.recaptchaToken) {
        // Fallback: verify if we have a token but no score (shouldn't happen in normal flow)
        console.log('⚠️ Verifying reCAPTCHA token in backend (token provided without score)');
        const recaptchaResult = await authService.verifyRecaptcha(userData.recaptchaToken, 'register');
        if (!recaptchaResult.success) {
          return {
            success: false,
            error: recaptchaResult.error || 'reCAPTCHA verification failed. Please try again.',
            type: 'recaptcha_error'
          };
        }
        console.log('✅ reCAPTCHA Enterprise verification successful. Score:', recaptchaResult.score);
        userData.recaptchaScore = recaptchaResult.score;
      } else {
        return {
          success: false,
          error: 'reCAPTCHA verification is required',
          type: 'recaptcha_missing'
        };
      }
      
      // First check Supabase connectivity
      const connectionCheck = await checkSupabaseConnection();
      if (!connectionCheck.connected) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase connection failed during signup:', connectionCheck.error);
        }
        return { 
          success: false, 
          error: connectionCheck.error || 'Unable to connect to authentication service. Please check your internet connection and try again.',
          type: connectionCheck.type || 'connection_error'
        };
      }
      
      console.log('✅ Supabase connection verified');
      
      // Prepare user metadata for profile creation
      const userMetadata = {
        full_name: userData.fullName || userData.full_name || email.split('@')[0],
        role: userData.role || 'va',
        skills: userData.skills || '',
        experience_level: userData.experienceLevel || userData.experience_level || '',
        hourly_rate: userData.hourlyRate || userData.hourly_rate || null,
        is_public_profile: true,
        is_verified: false,
        first_name: userData.firstName || userData.first_name || '',
        last_name: userData.lastName || userData.last_name || ''
      };

      console.log('📝 User metadata prepared:', { ...userMetadata, role: userMetadata.role });

      // Get the site URL for redirects
      const siteUrl = window.location.origin;
      
      // Validate that site URL is properly formatted
      if (!siteUrl.startsWith('http')) {
        console.error('Invalid site URL format:', siteUrl);
        return {
          success: false,
          error: 'Invalid site URL configuration. Please contact support.',
          type: 'config_error'
        };
      }
      
      // Check session state before signup
      const { data: sessionData } = await supabase.auth.getSession();
      console.log('🔍 Current session before signup:', sessionData?.session ? 'Active session found' : 'No active session');
      
      // If there's an existing session, sign out first to avoid conflicts
      if (sessionData?.session) {
        console.log('🚪 Signing out existing session');
        await supabase.auth.signOut();
      }
      
      console.log('🔐 Calling Supabase auth.signUp...');
      
      // Sign up with Supabase - configure to use custom email verification
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: userMetadata,
          // Point to our custom verification handler - this will receive the token_hash
          // Don't set this to undefined - that causes Supabase to auto-confirm emails!
          emailRedirectTo: `${window.location.origin}/auth/verify`,
          // Captcha token for bot protection
          captchaToken: userData.recaptchaToken || null
        }
      });

      if (error) {
        console.error('❌ Supabase auth.signUp failed:', error.message);
        // Enhanced error handling for signup with minimal logging
        if (error.message.includes('User already registered') || 
            error.message.includes('already been registered') ||
            error.message.includes('already registered')) {
          
          // Check if this is an orphaned auth record that can be cleaned up
          try {
            const { data: orphanedCheck, error: orphanError } = await supabase
              .rpc('check_email_orphaned', { p_email: email.trim().toLowerCase() });
            
            // Handle RPC function not existing
            if (orphanError && (orphanError.code === '42883' || orphanError.message.includes('function') || orphanError.message.includes('404'))) {
              // Skip orphan check and proceed with normal user exists handling
            } else if (orphanedCheck?.is_orphaned) {
              // For now, suggest manual cleanup since automated cleanup requires admin privileges
              return { 
                success: false, 
                error: 'This email has an incomplete account registration. Please contact support to clean up your previous registration, then try again.',
                type: 'orphaned_auth_record'
              };
            }
          } catch (orphanCheck) {
            console.error('Error checking for orphaned auth record:', orphanCheck);
          }
          
          // Keep security-sensitive error handling internal
          return { 
            success: false, 
            error: 'An account with this email already exists. Please sign in instead or use a different email address.',
            type: 'user_exists'
          };
        }
        if (error.message.includes('Password should be at least')) {
          return { 
            success: false, 
            error: 'Password must be at least 6 characters long.',
            type: 'weak_password'
          };
        }
        if (error.message.includes('Signup is disabled')) {
          return { 
            success: false, 
            error: 'Account registration is currently disabled. Please contact support.',
            type: 'signup_disabled'
          };
        }
        if (error.message.includes('Invalid email')) {
          return { 
            success: false, 
            error: 'Please enter a valid email address.',
            type: 'invalid_email'
          };
        }
        
        // For security reasons, log internally but provide a generic message
        console.error('Signup error (not exposed to user):', error.message);
        return { 
          success: false, 
          error: 'Unable to create account. Please try again or contact support.',
          type: 'signup_error'
        };
      }

      console.log('✅ Supabase auth.signUp successful. User created:', data?.user?.id);
      console.log('📧 Email confirmed:', data?.user?.email_confirmed_at ? 'Yes' : 'No');
      console.log('🔑 Session created:', data?.session ? 'Yes' : 'No');

      // Explicitly create user profile immediately after registration
      // This ensures the profile exists before the user clicks the verification link
      // IMPORTANT: Create profile WHILE session is active, BEFORE signing out
      if (data?.user) {
        try {
          console.log('👤 Starting profile creation process...');
          
          // Wait a moment for the database trigger to create the profile
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Check if profile already exists (should be created by trigger)
          console.log('🔍 Checking if profile exists from trigger...');
          const { data: existingProfile, error: profileCheckError } = await supabase
            .from('user_profiles')
            .select('id, email, full_name, role')
            .eq('id', data.user.id)
            .maybeSingle();
            
          if (profileCheckError) {
            console.error('❌ Error checking for existing profile:', profileCheckError);
          }
            
          if (existingProfile) {
            console.log('✅ Profile exists from trigger, updating with additional data');
            // Profile exists (created by trigger), update it with additional data
            const { error: updateError } = await supabase
              .from('user_profiles')
              .update({
                skills: userMetadata.skills,
                experience_level: userMetadata.experience_level,
                hourly_rate: userMetadata.hourly_rate,
                is_public_profile: userMetadata.is_public_profile,
                updated_at: new Date().toISOString()
              })
              .eq('id', data.user.id);
              
            if (updateError) {
              console.error('❌ Error updating profile with additional data:', updateError);
              // Continue anyway - basic profile exists
            } else {
              console.log('✅ Profile updated with additional data');
            }
          } else {
            console.log('⚠️ Profile does not exist, creating manually');
            // Profile doesn't exist yet (trigger failed), create it manually
            const profileData = {
              id: data.user.id,  // Explicitly set the ID
              email: email.trim().toLowerCase(),
              full_name: userMetadata.full_name,
              role: userMetadata.role,
              skills: userMetadata.skills,
              experience_level: userMetadata.experience_level,
              hourly_rate: userMetadata.hourly_rate,
              is_public_profile: userMetadata.is_public_profile,
              is_verified: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            // Direct insert with the active session
            const { data: insertedProfile, error: insertError } = await supabase
              .from('user_profiles')
              .insert(profileData)
              .select()
              .single();
              
            if (insertError) {
              console.error('❌ Direct profile insertion failed:', insertError);
              
              // Try fallback with createUserProfile method
              const profileResult = await authService.createUserProfile(data.user.id, profileData);
              
              if (!profileResult.success) {
                console.error('❌ Failed to create user profile during registration:', profileResult.error);
                // Don't fail registration for profile creation issues, but log it
              } else {
                console.log('✅ Profile created via fallback method');
              }
            } else {
              console.log('✅ Profile created via direct insertion:', insertedProfile);
            }
          }
        } catch (profileError) {
          console.error('❌ Error handling user profile during registration:', profileError);
          // Continue with signup but log the error - profile might be created by trigger
        }
      }

      // Supabase will send the built-in confirmation email automatically
      // No need to send custom verification email via Resend
      let verificationEmailSent = false;
      if (data?.user && !data.user.email_confirmed_at) {
        console.log('📧 Supabase will send built-in confirmation email automatically');
        verificationEmailSent = true; // Supabase handles email sending
      } else if (data?.user?.email_confirmed_at) {
        console.log('ℹ️ Email already confirmed, no verification needed');
        verificationEmailSent = false;
      }

      // CRITICAL: Always sign out after registration to prevent auto-login
      // Users must verify their email before they can access the system
      if (data?.user) {
        try {
          console.log('🚪 Signing out user to enforce email verification...');
          await supabase.auth.signOut();
          // Clear the session from the data object so UI doesn't think user is logged in
          data.session = null;
          console.log('✅ User signed out successfully');
        } catch (signOutError) {
          console.error('❌ Error signing out after signup:', signOutError);
          // Force sign out even if error occurs
          try {
            await supabase.auth.signOut({ scope: 'global' });
          } catch (forceSignOutError) {
            console.error('❌ Force sign out failed:', forceSignOutError);
          }
          data.session = null;
        }
      }

      // Send signup success notification (non-blocking)
      if (data?.user) {
        notificationService.notifySignupSuccess(
          data.user.email,
          userData.role || 'va',
          {
            userId: data.user.id,
            fullName: userData.fullName || userData.full_name || '',
            signupMethod: 'email_password'
          }
        ).catch(() => {});
      }

      console.log('🎉 Registration process completed successfully');

      // Return appropriate response based on email verification status
      if (!verificationEmailSent) {
        console.log('⚠️ Returning with verificationEmailSent: false');
        return {
          success: true,
          data,
          verificationEmailSent: false,
          warnings: [{
            type: 'email_not_sent',
            message: 'Your account was created, but we couldn\'t send a verification email. ' + 
                     'Please contact support to complete your email verification.'
          }]
        };
      }

      console.log('✅ Returning with verificationEmailSent: true');
      return { 
        success: true, 
        data,
        verificationEmailSent: true,
        message: 'Registration successful! Please check your email to verify your account before logging in.'
      };
    } catch (error) {
      console.error('💥 Unexpected error during registration:', error);
      
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError') ||
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Please check your internet connection or try again later.',
          type: 'network_error'
        };
      }
      
      // Handle unexpected signup error
      return { 
        success: false, 
        error: 'An unexpected error occurred during signup. Please try again.',
        type: 'unexpected_error'
      };
    }
  },

  // Enhanced sign out
  signOut: async () => {
    try {
      // Get current user before signing out to invalidate their cache
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      const { error } = await supabase.auth.signOut();
      
      if (error) {
        return { 
          success: false, 
          error: error.message,
          type: 'signout_error'
        };
      }

      // Invalidate all user-related cache
      if (userId && cacheService.isAvailable()) {
        await Promise.all([
          cacheService.invalidateUserProfile(userId),
          cacheService.invalidateUserSessions(userId),
          cacheService.invalidateJobRecommendations(userId),
        ]);
        if (process.env.NODE_ENV === 'development') {
          console.log('[authService] User cache invalidated on logout:', userId);
        }
      }

      return { success: true };
     
    } catch (_) {
      // Handle sign out error
      return { 
        success: false, 
        error: 'Failed to sign out properly. Please try again.',
        type: 'unexpected_error'
      };
    }
  },

  // Enhanced session management
  getSession: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        return { 
          success: false, 
          error: error.message,
          type: 'session_error'
        };
      }

      return { success: true, data };
    } catch (error) {
      // Handle session error
      console.error('Session retrieval error:', error);
      return { 
        success: false, 
        error: 'Failed to retrieve session information',
        type: 'session_error'
      };
    }
  },

  // Enhanced user retrieval
  getCurrentUser: async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        return { 
          success: false, 
          error: error.message,
          type: 'auth_error'
        };
      }
      
      if (!data?.user) {
        return { 
          success: false, 
          error: 'User not found',
          type: 'profile_fetch_error'
        };
      }
      
      return { success: true, data: data.user };
    } catch {
      // Handle user fetch error
      return { 
        success: false, 
        error: 'Failed to get current user information',
        type: 'user_fetch_error'
      };
    }
  },

  // Enhanced profile management with better error handling
  getUserProfile: async (userId) => {
    try {
      // Try to get from cache first
      if (cacheService.isAvailable()) {
        const cachedProfile = await cacheService.getUserProfile(userId);
        if (cachedProfile) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[authService] User profile loaded from cache:', userId);
          }
          return { success: true, data: cachedProfile };
        }
      }

      // Cache miss - fetch from database
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        if (error.message.includes('relation "public.user_profiles" does not exist')) {
          return { 
            success: false, 
            error: 'Database schema not properly initialized. Please contact support.',
            type: 'schema_error'
          };
        }
        return { 
          success: false, 
          error: error.message,
          type: 'database_error'
        };
      }

      // Cache the profile for future requests
      if (data && cacheService.isAvailable()) {
        await cacheService.setUserProfile(userId, data);
        if (process.env.NODE_ENV === 'development') {
          console.log('[authService] User profile cached:', userId);
        }
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Please check your internet connection or try again later.',
          type: 'network_error'
        };
      }
      
      // Handle profile fetch error
      return { 
        success: false, 
        error: 'Failed to load user profile',
        type: 'profile_fetch_error'
      };
    }
  },

  // Enhanced profile creation - improved to work with RLS policies and handle errors better
  createUserProfile: async (userId, profileData) => {
    try {
      // First check if the user is authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData?.session) {
        console.error('No active session found when creating profile');
        
        // If no session, we cannot create profile due to RLS policies
        // This should only happen if called incorrectly - profile creation should happen with active session
        return { 
          success: false, 
          error: 'Authentication required to create profile. Profile creation must happen with an active session.',
          type: 'auth_required',
          details: 'No session found - cannot bypass RLS policies'
        };
      }

      // First, check if profile already exists to avoid conflicts
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
        
      if (checkError) {
        console.error('Error checking existing profile:', checkError);
      } else if (existingProfile) {
        // Update existing profile
        const { data: updatedProfile, error: updateError } = await supabase
          .from('user_profiles')
          .update({
            ...profileData,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId)
          .select()
          .single();
          
        if (updateError) {
          console.error('Error updating existing profile:', updateError);
          return { 
            success: false, 
            error: updateError.message,
            type: 'profile_update_error'
          };
        }
        
        return { success: true, data: updatedProfile };
      }

      // Create new profile with explicit ID
      const fullProfileData = {
        id: userId,  // Explicitly set the user ID
        ...profileData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: insertedProfile, error: insertError } = await supabase
        .from('user_profiles')
        .insert(fullProfileData)
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting new profile:', insertError);
        return { 
          success: false, 
          error: insertError.message,
          type: 'profile_insert_error'
        };
      }

      return { success: true, data: insertedProfile };
    } catch (err) {
      console.error('Unexpected error in createUserProfile:', err);
      // Handle unexpected profile creation error
      return { 
        success: false, 
        error: 'Failed to create user profile: ' + (err.message || 'Unknown error'),
        type: 'profile_creation_error'
      };
    }
  },

  // Enhanced profile update
  updateUserProfile: async (userId, updates) => {
    try {
      if (!userId) {
        return { 
          success: false, 
          error: 'User ID is required',
          type: 'invalid_input'
        };
      }

      if (!updates || typeof updates !== 'object') {
        return { 
          success: false, 
          error: 'Invalid profile data',
          type: 'invalid_input'
        };
      }

      // Handle work_experience specially to avoid potential JSON stringification issues
      let updateData = { ...updates };
      
      // Check if work_experience is present and properly format it
      if (updateData.work_experience !== undefined) {
        try {
          // Ensure it's a proper array
          if (!Array.isArray(updateData.work_experience)) {
            updateData.work_experience = [];
          }
          
          // Ensure it's properly stringifiable before sending to Supabase
          JSON.stringify(updateData.work_experience);
        } catch (jsonError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error serializing work_experience:', jsonError);
          }
          // Work experience not serializable, use empty array as fallback
          updateData.work_experience = [];
        }
      }
      
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Profile update error:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
        }

        // Handle specific error types
        if (error.code === '42P01') {
          // Table doesn't exist
          return { 
            success: false, 
            error: 'Database table not found. Please contact support.',
            type: 'database_error'
          };
        }

        if (error.message.includes('row level security') || error.message.includes('RLS')) {
          // RLS policy violation
          return { 
            success: false, 
            error: 'Permission denied. You do not have access to update this profile. Please log out and back in.',
            type: 'rls_error'
          };
        }

        if (error.message.includes('UNIQUE constraint failed')) {
          // Unique constraint violation
          return { 
            success: false, 
            error: 'This value already exists. Please try a different value.',
            type: 'constraint_error'
          };
        }

        // Handle profile update error
        return { 
          success: false, 
          error: error.message || 'Failed to update profile',
          type: 'database_error'
        };
      }

      if (!data) {
        if (process.env.NODE_ENV === 'development') {
          console.error('No data returned from profile update');
        }
        return { 
          success: false, 
          error: 'Profile update did not return data. Please try again.',
          type: 'database_error'
        };
      }

      // Invalidate cache after successful update
      if (cacheService.isAvailable()) {
        await cacheService.invalidateUserProfile(userId);
        // Also invalidate job recommendations as skills may have changed
        await cacheService.invalidateJobRecommendations(userId);
        if (process.env.NODE_ENV === 'development') {
          console.log('[authService] Profile cache invalidated after update:', userId);
        }
      }

      return { success: true, data };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Profile update error:', error);
      }
      
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Please check your internet connection.',
          type: 'network_error'
        };
      }
      
      // Handle profile update error
      return { 
        success: false, 
        error: error?.message || 'Failed to update profile',
        type: 'unexpected_error'
      };
    }
  },

  // Custom password reset without Supabase dependency - Security enhanced
  resetPassword: async (email) => {
    try {
      // Check if we've sent a reset email recently to prevent duplicates
      const normalizedEmail = email.trim().toLowerCase();
      
      // Use secure database function for password reset lookup
      const { data: userDataArray, error: lookupError } = await supabase
        .rpc('get_user_for_password_reset', { user_email: normalizedEmail });
      
      if (lookupError) {
        console.error('Error looking up user for password reset:', lookupError);
        // Still return success to prevent user enumeration
        return { 
          success: true,
          message: "If an account exists with this email, password reset instructions will be sent."
        };
      }
      
      const userData = userDataArray?.[0]; // RPC returns an array
      
      // If we've sent a reset email in the last 5 minutes, don't send another one
      // But still return success to not leak user existence
      if (userData?.reset_password_sent_at) {
        const sentAt = new Date(userData.reset_password_sent_at);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        
        if (sentAt > fiveMinutesAgo) {
          return { 
            success: true,
            message: "If an account exists with this email, password reset instructions will be sent."
          };
        }
      }
      
      // Generate secure reset token if user exists
      if (userData?.user_id) {
        console.log('Password reset: User found with ID:', userData.user_id);
        
        // Generate a secure random token
        const resetToken = `reset_${Math.random().toString(36).substring(2)}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2)}`;
        const tokenExpiry = new Date(Date.now() + 60 * 60 * 1000); // 1 hour expiry
        
        console.log('Generated reset token, attempting RPC call for:', normalizedEmail);
        
        // Use secure database function to update the reset token
        const { data: updateResult, error: updateError } = await supabase
          .rpc('update_password_reset_token', { 
            user_email: normalizedEmail,
            reset_token: resetToken,
            token_expires_at: tokenExpiry.toISOString()
          });

        console.log('RPC update_password_reset_token result:', { updateResult, updateError });

        if (updateError) {
          console.error('Error updating password reset token:', updateError);
          // Still return success to prevent user enumeration
          return { 
            success: true,
            message: "If an account exists with this email, password reset instructions will be sent."
          };
        }

        // Create reset URL with token
        const resetUrl = `${window.location.origin}/auth/reset-password?token=${resetToken}&email=${encodeURIComponent(normalizedEmail)}`;
        
        console.log('Sending password reset email to:', normalizedEmail);
        
        // Send custom reset email
        try {
          const fullName = userData.full_name || normalizedEmail.split('@')[0];
          const emailResult = await emailService.sendPasswordResetEmail(
            normalizedEmail,
            resetUrl,
            fullName
          );
          console.log('Password reset email result:', emailResult);
        } catch (emailError) {
          console.error('Custom reset email failed:', emailError);
          // Don't expose email service errors to user
        }

        // Send legacy notification
        try {
          await notificationService.notifyPasswordResetRequest(normalizedEmail);
        } catch (notifyError) {
          console.error('Notification service error (not exposing to user):', notifyError);
        }
      } else {
        console.log('Password reset: No user found for email:', normalizedEmail);
      }

      // Always return success to prevent user enumeration
      return { 
        success: true,
        message: "If an account exists with this email, password reset instructions will be sent."
      };
    } catch (resetError) {
      // Handle unexpected errors without revealing user existence
      console.error('Unexpected reset password error:', resetError);
      
      // Still don't reveal user existence
      return { 
        success: true,
        message: "If an account exists with this email, password reset instructions will be sent."
      };
    }
  },

  // Enhanced method to verify email
  verifyEmail: async (token, email) => {
    try {
      // First, check if a user with this email exists in the user_profiles table
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, email, is_verified')
        .eq('email', email.toLowerCase())
        .maybeSingle();
      
      let userId = null;
      let authData = null;
      let authError = null;
      
      // If we found a profile, use its ID
      if (profileData && !profileError) {
        userId = profileData.id;
        authData = {
          user: {
            id: profileData.id,
            email: profileData.email
          }
        };
      } else {
        // If no profile found by email, try to get the user from auth system
        try {
          // Try to verify if the token itself is a valid user ID
          const { data: userByToken, error: tokenError } = await supabase
            .from('user_profiles')
            .select('id, email')
            .eq('id', token)
            .maybeSingle();
            
          if (userByToken && !tokenError && userByToken.email.toLowerCase() === email.toLowerCase()) {
            // The token is a valid user ID and matches the email
            userId = token;
            authData = {
              user: {
                id: token,
                email: userByToken.email
              }
            };
          } else {
            // Try to get user by email from auth system
            try {
              await supabase.auth.signInWithPassword({
                email: email.toLowerCase(),
                password: 'INTENTIONAL_WRONG_PASSWORD_FOR_VERIFICATION_CHECK'
              });
            } catch (signInError) {
              // Check if the error indicates the user exists
              if (signInError.message && (
                  signInError.message.includes('Invalid login credentials') ||
                  signInError.message.includes('Email not confirmed')
              )) {
                // User exists, try to get their ID
                const { data: authUser } = await supabase.auth.getUser();
                if (authUser?.user) {
                  userId = authUser.user.id;
                  authData = { user: authUser.user };
                }
              }
            }
          }
        } catch (checkError) {
          console.error('Error checking user existence:', checkError);
          // Continue with verification attempt
        }
      }

      if (authError || !authData?.user) {
        console.error('Error getting auth user:', authError || 'User not found');
        return {
          success: false,
          error: 'Invalid verification token. Please request a new verification email.',
          type: 'verification_error'
        };
      }
      
      // Verify the email matches if we have it from authData
      if (authData.user.email && authData.user.email.toLowerCase() !== email.toLowerCase()) {
        console.error('Email mismatch during verification');
        return {
          success: false,
          error: 'Email verification failed. Please request a new verification email.',
          type: 'verification_error'
        };
      }
      
      // Now check if we found a valid user
      if (!userId) {
        console.error('Could not find user with email:', email);
        return {
          success: false,
          error: 'User not found with this email. Please check your registration details.',
          type: 'verification_error'
        };
      }
      
      // Check if the user profile exists with the found user ID
      let userData;
      const { data: userProfileData, error: userError } = await supabase
        .from('user_profiles')
        .select('id, is_verified, email')
        .eq('id', userId)
        .maybeSingle();
      
      // If profile doesn't exist yet, create it from auth metadata or provided email
      if (userError || !userProfileData) {
        // Try to get user metadata from auth if available
        let metadata = {};
        try {
          // Try to get user metadata if we have an active session
          const { data: userMetadata } = await supabase.auth.getUser();
          metadata = userMetadata?.user?.user_metadata || {};
         
        } catch (metadataError) {
          // Continue without metadata
        }
        
        // Create profile data with fallbacks
        const profileData = {
          id: userId, // Use the found user ID
          email: email.toLowerCase(), // Use the provided email
          full_name: metadata.full_name || email.split('@')[0],
          role: metadata.role || 'va',
          skills: metadata.skills || '',
          experience_level: metadata.experience_level || '',
          hourly_rate: metadata.hourly_rate || null,
          is_public_profile: true,
          is_verified: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Insert the profile directly
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert(profileData);
        
        if (insertError) {
          console.error('Error creating user profile during verification:', insertError);
          // If we can't create the profile, return an error
          return {
            success: false,
            error: 'Failed to create user profile. Please contact support.',
            type: 'profile_creation_error'
          };
        }
        
        // Set userData for later use
        userData = profileData;
      } else if (!userProfileData.is_verified) {
        // Update the user profile to mark as verified if not already
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            is_verified: true,
            updated_at: new Date().toISOString() 
          })
          .eq('id', userId);
        
        if (updateError) {
          console.error('Error updating verification status:', updateError);
          // Continue with verification but log the error
        }
      }
      
      // Update the auth user's email_confirmed_at using admin API
      if (authData?.user) {
        try {
          // First, update the user profile
          const { error: updateProfileError } = await supabase
            .from('user_profiles')
            .update({ 
              is_verified: true,
              updated_at: new Date().toISOString() 
            })
            .eq('id', userId);
          
          if (updateProfileError) {
            console.error('Error updating profile verification status:', updateProfileError);
          }

          // Update the auth user's email confirmation status using service role
          // This requires admin privileges, so we'll create a database function to handle it
          const { error: confirmEmailError } = await supabase.rpc('confirm_user_email', {
            user_id: userId
          });

          if (confirmEmailError) {
            console.warn('Could not update auth email confirmation status:', confirmEmailError);
            // Continue anyway since profile is verified
          }
        } catch (updateError) {
          console.error('Error updating user confirmation status:', updateError);
          // Continue with verification but log the error
        }
      }

      // Set the final userData value for the return
      if (!userData) {
        userData = userProfileData;
      }

      // Send welcome email after successful verification
      if (authData?.user) {
        try {
          // Get the latest profile data
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('full_name, role')
            .eq('id', userId)
            .single();
            
          const fullName = profileData?.full_name || email.split('@')[0] || 'User';
          const userRole = profileData?.role || 'va';
          
          await emailService.sendWelcomeEmail(
            email,
            fullName,
            userRole
          );
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError);
          // Failed to send welcome email, but continue with verification
        }
      }

      return { 
        success: true, 
        data: { 
          user: authData?.user, 
          profile: userData || { id: token } 
        } 
      };
    } catch (err) {
      console.error('Verification error:', err);
      // Handle verification error
      return { 
        success: false, 
        error: 'Failed to verify email. Please try again.', 
        type: 'verification_error' 
      };
    }
  },

  // New method to resend verification email
  resendVerificationEmail: async (email) => {
    try {
      // Only send custom verification email using Resend (not Supabase native)
      // This ensures consistent verification flow through /auth/verify route
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, full_name, role')
        .eq('email', email.trim().toLowerCase())
        .single();

      if (profileError || !profileData) {
        return {
          success: false,
          error: 'User not found. Please check your email address and try again.',
          type: 'user_not_found'
        };
      }

      // Generate a verification URL with the user ID as the token
      const verificationUrl = `${window.location.origin}/auth/verify?token=${profileData.id}&email=${encodeURIComponent(email)}&type=signup`;
      
      const emailResult = await emailService.sendVerificationEmail(
        email,
        verificationUrl,
        profileData.full_name || email.split('@')[0],
        profileData.role || 'va'
      );

      if (!emailResult.success) {
        return {
          success: false,
          error: 'Failed to send verification email. Please try again.',
          type: 'email_send_error'
        };
      }

      // Update the verification_email_sent tracking
      try {
        await supabase
          .from('user_profiles')
          .update({ 
            verification_email_sent: true,
            verification_email_sent_at: new Date().toISOString() 
          })
          .eq('id', profileData.id);
      } catch (updateError) {
        console.error('Error updating verification email tracking:', updateError);
        // Don't fail the operation for tracking updates
      }

      return { success: true };
    } catch (error) {
      console.error('Error in resendVerificationEmail:', error);
      // Handle resend verification error
      return {
        success: false,
        error: 'Failed to resend verification email. Please try again.',
        type: 'resend_error'
      };
    }
  },

  // Custom password update with token validation using Edge Function
  updatePassword: async (password, resetToken, email) => {
    try {
      // Validate input parameters
      if (!resetToken || !email || !password) {
        return { 
          success: false, 
          error: 'Invalid password reset link. Please request a new password reset.',
          type: 'invalid_reset_link'
        };
      }

      // Use Edge Function to update password with admin privileges
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resetToken,
          email: email.toLowerCase().trim(),
          newPassword: password
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return { 
          success: false, 
          error: result.error || 'Failed to update password. Please try again.',
          type: 'password_update_error'
        };
      }

      // Send password change confirmation email
      try {
        // Get user profile for full name
        const profileResult = await authService.getUserProfile(result.userId);
        const profile = profileResult.data;
        
        await emailService.sendPasswordChangeConfirmation(
          email,
          profile?.full_name || email.split('@')[0],
          profile?.role || 'va'
        );
       
      } catch (emailError) {
        // Failed to send password change confirmation, but continue
      }

      return { 
        success: true,
        message: 'Password updated successfully. You can now sign in with your new password.'
      };
    } catch (updateError) {
      // Handle password update error
      console.error('Password update error:', updateError);
      return { 
        success: false, 
        error: 'Failed to update password. Please try again.',
        type: 'password_update_error'
      };
    }
  },

  // Enhanced session refresh
  refreshSession: async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        return { 
          success: false, 
          error: error.message,
          type: 'refresh_error'
        };
      }
      
      return { success: true, data };
     
    } catch (_) {
      // Handle session error
      return { 
        success: false, 
        error: 'Failed to refresh session',
        type: 'refresh_error'
      };
    }
  },

  // New utility method to test database connection
  testConnection: async () => {
    return checkSupabaseConnection();
  },

  // New method to check notification service health
  checkNotificationService: async () => {
    return await notificationService.healthCheck();
  }
};

export default authService;
